import { useState, useEffect, useRef, useCallback } from 'react'
import { Card, Form, Button, Row, Col, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { systemApi } from '../services/api'

interface LogLine {
  raw: string
  timestamp: string | null
  level: string | null
  source: string | null
  message: string
  severity: 'error' | 'warning' | 'notice' | 'security' | 'info'
  pid?: string
}

interface LogFile {
  name: string
  path: string
  size: number
  size_human: string
  modified: string
}

export default function Logs() {
  const [logFiles, setLogFiles] = useState<LogFile[]>([])
  const [selectedFile, setSelectedFile] = useState('messages')
  const [logLines, setLogLines] = useState<LogLine[]>([])
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshInterval, setRefreshInterval] = useState(5)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState('')
  const [sinceFilter, setSinceFilter] = useState('')
  const [lineCount, setLineCount] = useState(500)

  const logContainerRef = useRef<HTMLDivElement>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Load log files list
  useEffect(() => {
    loadLogFiles()
  }, [])

  // Auto-refresh logic
  useEffect(() => {
    if (autoRefresh) {
      refreshTimerRef.current = setInterval(() => {
        loadLogs(true)
      }, refreshInterval * 1000)
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
      }
    }
  }, [autoRefresh, refreshInterval, selectedFile])

  const loadLogFiles = async () => {
    try {
      const res = await systemApi.getLogFiles()
      setLogFiles(res.data.files || [])
    } catch {
      toast.error('Fehler beim Laden der Log-Dateien')
    }
  }

  const loadLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await systemApi.viewLog({
        file: selectedFile,
        lines: lineCount,
        search: searchQuery || undefined,
        level: levelFilter || undefined,
        since: sinceFilter || undefined
      })
      setLogLines(res.data.lines || [])

      // Auto-scroll to bottom
      if (logContainerRef.current) {
        setTimeout(() => {
          if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
          }
        }, 100)
      }
    } catch (error: any) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Fehler beim Laden der Logs')
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [selectedFile, lineCount, searchQuery, levelFilter, sinceFilter])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    loadLogs()
  }

  const clearFilters = () => {
    setSearchQuery('')
    setLevelFilter('')
    setSinceFilter('')
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'danger'
      case 'warning': return 'warning'
      case 'notice': return 'info'
      case 'security': return 'primary'
      default: return 'secondary'
    }
  }

  const getSeverityBg = (severity: string) => {
    switch (severity) {
      case 'error': return 'bg-danger bg-opacity-10'
      case 'warning': return 'bg-warning bg-opacity-10'
      case 'security': return 'bg-primary bg-opacity-10'
      default: return ''
    }
  }

  const highlightSearch = (text: string) => {
    if (!searchQuery) return text
    try {
      const regex = new RegExp(`(${searchQuery})`, 'gi')
      return text.replace(regex, '<mark>$1</mark>')
    } catch {
      return text
    }
  }

  const downloadLog = () => {
    const content = logLines.map(l => l.raw).join('\n')
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${selectedFile}-${new Date().toISOString().slice(0, 10)}.log`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4><i className="bi bi-journal-text me-2"></i>Asterisk Logs</h4>
        <div className="d-flex gap-2">
          <Button
            variant={autoRefresh ? 'success' : 'outline-secondary'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Auto-Refresh aktiv' : 'Auto-Refresh starten'}
          >
            <i className={`bi ${autoRefresh ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
            {autoRefresh ? 'Stop' : 'Live'}
          </Button>
          <Button variant="outline-primary" size="sm" onClick={() => loadLogs()} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1"></i>Aktualisieren
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={downloadLog} disabled={logLines.length === 0}>
            <i className="bi bi-download me-1"></i>Download
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small mb-1">Log-Datei</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedFile}
                    onChange={(e) => setSelectedFile(e.target.value)}
                  >
                    {logFiles.length > 0 ? (
                      logFiles.map(f => (
                        <option key={f.name} value={f.name}>
                          {f.name} ({f.size_human})
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="messages">messages</option>
                        <option value="full">full</option>
                        <option value="queue_log">queue_log</option>
                      </>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Suche (Regex)</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="text"
                      placeholder="z.B. SECURITY|ERROR"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small mb-1">Level</Form.Label>
                  <Form.Select
                    size="sm"
                    value={levelFilter}
                    onChange={(e) => setLevelFilter(e.target.value)}
                  >
                    <option value="">Alle</option>
                    <option value="ERROR">ERROR</option>
                    <option value="WARNING">WARNING</option>
                    <option value="NOTICE">NOTICE</option>
                    <option value="SECURITY">SECURITY</option>
                    <option value="VERBOSE">VERBOSE</option>
                    <option value="DEBUG">DEBUG</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small mb-1">Zeitraum</Form.Label>
                  <Form.Select
                    size="sm"
                    value={sinceFilter}
                    onChange={(e) => setSinceFilter(e.target.value)}
                  >
                    <option value="">Alle</option>
                    <option value="5m">Letzte 5 Min</option>
                    <option value="15m">Letzte 15 Min</option>
                    <option value="1h">Letzte Stunde</option>
                    <option value="6h">Letzte 6 Stunden</option>
                    <option value="1d">Letzter Tag</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={1}>
                <Form.Group>
                  <Form.Label className="small mb-1">Zeilen</Form.Label>
                  <Form.Select
                    size="sm"
                    value={lineCount}
                    onChange={(e) => setLineCount(parseInt(e.target.value))}
                  >
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                    <option value={2000}>2000</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <div className="d-flex gap-1">
                  <Button variant="primary" size="sm" type="submit" disabled={loading} className="flex-grow-1">
                    {loading ? <Spinner size="sm" /> : <><i className="bi bi-search me-1"></i>Suchen</>}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={clearFilters} title="Filter zurücksetzen">
                    <i className="bi bi-x-lg"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Auto-refresh indicator */}
      {autoRefresh && (
        <Alert variant="success" className="py-2 d-flex align-items-center justify-content-between">
          <div>
            <Spinner size="sm" className="me-2" />
            <strong>Live-Modus:</strong> Logs werden alle {refreshInterval} Sekunden aktualisiert
          </div>
          <div>
            <Form.Select
              size="sm"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
              style={{ width: '100px' }}
            >
              <option value={2}>2 Sek</option>
              <option value={5}>5 Sek</option>
              <option value={10}>10 Sek</option>
              <option value={30}>30 Sek</option>
            </Form.Select>
          </div>
        </Alert>
      )}

      {/* Log output */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center py-2">
          <div>
            <i className="bi bi-terminal me-2"></i>
            <strong>{selectedFile}</strong>
            <Badge bg="secondary" className="ms-2">{logLines.length} Zeilen</Badge>
          </div>
          <div className="d-flex gap-2">
            <Badge bg="danger">ERROR</Badge>
            <Badge bg="warning" text="dark">WARNING</Badge>
            <Badge bg="info">NOTICE</Badge>
            <Badge bg="primary">SECURITY</Badge>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div
            ref={logContainerRef}
            className="log-container"
            style={{
              height: '60vh',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4'
            }}
          >
            {loading && logLines.length === 0 ? (
              <div className="text-center py-5">
                <Spinner />
                <p className="mt-2 text-muted">Lade Logs...</p>
              </div>
            ) : logLines.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-journal-x" style={{ fontSize: '3rem' }}></i>
                <p className="mt-2">Keine Log-Einträge gefunden</p>
                <Button variant="outline-light" size="sm" onClick={() => loadLogs()}>
                  Logs laden
                </Button>
              </div>
            ) : (
              <table className="table table-dark table-sm table-hover mb-0" style={{ fontSize: '0.75rem' }}>
                <tbody>
                  {logLines.map((line, idx) => (
                    <tr key={idx} className={getSeverityBg(line.severity)}>
                      <td style={{ width: '140px', whiteSpace: 'nowrap', color: '#888' }}>
                        {line.timestamp || ''}
                      </td>
                      <td style={{ width: '80px' }}>
                        {line.level && (
                          <Badge
                            bg={getSeverityColor(line.severity)}
                            style={{ fontSize: '0.65rem', fontWeight: 'normal' }}
                          >
                            {line.level}
                          </Badge>
                        )}
                      </td>
                      <td style={{ width: '200px', color: '#9cdcfe', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {line.source || ''}
                      </td>
                      <td
                        style={{
                          wordBreak: 'break-all',
                          color: line.severity === 'error' ? '#f48771' :
                                 line.severity === 'warning' ? '#dcdcaa' :
                                 line.severity === 'security' ? '#9cdcfe' : '#d4d4d4'
                        }}
                        dangerouslySetInnerHTML={{ __html: highlightSearch(line.message) }}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Quick search hints */}
      <div className="mt-3 text-muted" style={{ fontSize: '0.85rem' }}>
        <i className="bi bi-lightbulb me-2"></i>
        <strong>Tipp:</strong> Suche mit Regex-Mustern wie <code>SECURITY.*SuccessfulAuth</code> oder <code>ERROR|FAILED</code>
      </div>
    </div>
  )
}
